import React, { FunctionComponent, useCallback, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { Snackbar, SnackbarType } from '@lux/snackbar';
import { hideHeaderFlashMessage } from '@client/redux/modules/flashMessage/actions';
import { getHeaderFlashMessage } from '@client/redux/modules/flashMessage/selectors';
import classNames from 'classnames/bind';

import styles from './styles.scss';

const cn = classNames.bind(styles);

const FADE_DURATION_MS = 2000; // ms
const HIDE_TIMER_MS = 5000; // ms

interface FlashMessagesProps {
    hasSmartBanner?: boolean;
}

export const FlashMessages: FunctionComponent<FlashMessagesProps> = ({
    hasSmartBanner = false,
}) => {
    const timer = useRef<ReturnType<typeof setTimeout>>(null);
    const dispatch = useDispatch();

    const { type, message, action } = useSelector(getHeaderFlashMessage);

    const clearTimer = useCallback(() => {
        if (timer.current) {
            clearTimeout(timer.current);
            timer.current = null;
        }
    }, []);

    useEffect(() => {
        if (!type || !message) {
            return clearTimer;
        }

        clearTimer();

        if (!action) {
            timer.current = setTimeout(() => dispatch(hideHeaderFlashMessage()), HIDE_TIMER_MS);
        }

        return clearTimer;
    }, [type, message, action, dispatch, clearTimer]);

    return (
        <TransitionGroup>
            {type && message && (
                <CSSTransition
                    component="div"
                    classNames={styles}
                    timeout={{
                        enter: FADE_DURATION_MS,
                        exit: FADE_DURATION_MS,
                    }}
                >
                    <div className={cn('wrapper', 'flash_messages', { hasSmartBanner })}>
                        <Snackbar
                            type={type as SnackbarType}
                            content={message}
                            action={
                                action && {
                                    text: action.text,
                                    ctaCallback: action.callback,
                                }
                            }
                            visible
                        />
                    </div>
                </CSSTransition>
            )}
        </TransitionGroup>
    );
};

export default FlashMessages;
